import { apiService } from "@/services/apiService";
import { ObjectKeyHasher } from "@repo/lib/keyHasher";
import {
  RepoCache,
  type GetObservableOptions,
} from "@repo/shared-client/repoCache";
import type {
  LaserfichePushType as APIPushType,
  UploadTypeId as APIUploadType,
} from "@repo/shared/api";
import { omitFields } from "@repo/shared/utils";
import type {
  LaserficheConfiguration,
  LaserficheConnection,
  LaserficheConnectionDto,
  LaserficheDirectory,
  LaserficheInspectDirectoryItem,
  LaserficheInspectField,
  LaserficheInspectTag,
  LaserficheInspectTemplate,
  LaserficheInternalConfiguration,
  LaserficheMetadata,
  LaserficheMetadataConfigKey,
  LaserfichePutMetadata,
} from "@repo/types";
import { cacheDefaults } from "./common";
import type { AnyClock } from "@repo/lib/clock";
import type { Observable } from "rxjs";
import { baseRepo } from "./baseRepo";

export interface FieldAssignment {
  type: "constant" | "key";
  constant: string;
  key: string;
}

export type FieldAssignmentSet = Record<number, FieldAssignment>;
export interface UpdateLaserficheMetadata {
  key: LaserficheMetadataConfigKey;
  config: LaserfichePutMetadata;
}

const teamQuery = (teamId?: string | null) => (teamId ? { teamId } : {});

type LaserficheListConnectionsKey = { type: "connections"; tenantId: string };
type LaserficheGetIntegrationKey = {
  type: "integration";
  tenantId: string;
  teamId: string | null;
};
type LaserficheGetMetadataKey = {
  type: "meta";
  tenantId: string;
  teamId: string | null;
};
type LaserficheGetDirectoryKey = {
  type: "directory";
  tenantId: string;
  teamId: string | null;
  directoryId: number;
};
type LaserficheKey =
  | LaserficheListConnectionsKey
  | LaserficheGetIntegrationKey
  | LaserficheGetMetadataKey
  | LaserficheGetDirectoryKey;

export type LaserficheListConnectionsDto = Record<
  string,
  LaserficheConnectionDto
>;
export type LaserficheGetIntegrationDto = {
  connection: LaserficheConnectionDto | null;
  directories: {
    orphanSigned?: LaserficheDirectory;
    primary?: LaserficheDirectory;
  } | null;
  metadata: {
    documentType?: Record<string, LaserficheMetadata>;
    orphanSigned?: LaserficheMetadata;
    signed?: LaserficheMetadata;
    minutes?: LaserficheMetadata;
    documents?: LaserficheMetadata;
    agendas?: LaserficheMetadata;
  } | null;
  internal: LaserficheInternalConfiguration | null;
};
export type LaserficheGetMetadataDto = {
  tags: LaserficheInspectTag[];
  templates: LaserficheInspectTemplate[];
  fields: LaserficheInspectField[];
};
export type LaserficheGetDirectoryDto = LaserficheInspectDirectoryItem[] | null;

type LaserficheData =
  | LaserficheListConnectionsDto
  | LaserficheGetIntegrationDto
  | LaserficheGetMetadataDto
  | LaserficheGetDirectoryDto;

export class LaserficheIntegrationRepository {
  private cache: RepoCache<string, LaserficheData, ObjectKeyHasher, AnyClock>;
  private keyHasher: ObjectKeyHasher;

  private async internalListConnections(
    tenantId: string
  ): Promise<LaserficheListConnectionsDto> {
    const connections = (
      await apiService.v1.integrationLaserficheListConnections(tenantId)
    ).data;
    return connections;
  }
  private async internalGetIntegration(
    tenantId: string,
    teamId: string | null
  ): Promise<LaserficheGetIntegrationDto> {
    const query = teamQuery(teamId);
    const connection = (
      await apiService.v1.integrationLaserficheGetConnection(tenantId, query)
    ).data;
    const directories = (
      await apiService.v1.integrationLaserficheGetConfigDirectory(
        tenantId,
        query
      )
    ).data;
    const metadata = (
      await apiService.v1.integrationLaserficheGetConfigMetadata(
        tenantId,
        query
      )
    ).data;
    const internal = (
      await apiService.v1.integrationLaserficheGetInternalConfig(
        tenantId,
        query
      )
    ).data;
    return { connection, directories, metadata, internal };
  }
  private async internalGetMetadata(
    tenantId: string,
    teamId: string | null
  ): Promise<LaserficheGetMetadataDto> {
    const query = teamQuery(teamId);
    const tags = (
      await apiService.v1.integrationLaserficheInspectTags(tenantId, query)
    ).data;
    const templates = (
      await apiService.v1.integrationLaserficheInspectTemplates(tenantId, query)
    ).data;
    const fields = (
      await apiService.v1.integrationLaserficheInspectFields(tenantId, query)
    ).data;
    return { tags, templates, fields };
  }
  private async internalGetDirectory(
    tenantId: string,
    teamId: string | null,
    directoryId: number
  ): Promise<LaserficheGetDirectoryDto> {
    const query = { ...teamQuery(teamId), directoryId };
    const directory = (
      await apiService.v1.integrationLaserficheInspectDirectory(tenantId, query)
    ).data;
    return directory;
  }

  constructor() {
    this.keyHasher = new ObjectKeyHasher({ sortKeys: false });
    this.cache = new RepoCache({
      requestKeyHasher: this.keyHasher,
      expiresAfterSeconds: cacheDefaults.expiresAfterSeconds,
      purgeExpirationAfterSeconds: cacheDefaults.purgeExpiredAfterSeconds,
      fetch: async ({ requestKey, publish }) => {
        const key = requestKey as LaserficheKey;

        switch (key.type) {
          case "connections": {
            const connections = await this.internalListConnections(
              key.tenantId
            );
            publish(this.keyHasher.hash(key), connections);
            break;
          }
          case "integration": {
            const integration = await this.internalGetIntegration(
              key.tenantId,
              key.teamId
            );
            publish(this.keyHasher.hash(key), integration);
            break;
          }
          case "meta": {
            const meta = await this.internalGetMetadata(
              key.tenantId,
              key.teamId
            );
            publish(this.keyHasher.hash(key), meta);
            break;
          }
          case "directory": {
            const directory = await this.internalGetDirectory(
              key.tenantId,
              key.teamId,
              key.directoryId
            );
            publish(this.keyHasher.hash(key), directory);
            break;
          }
        }
      },
      collect: ({ requestKey, table }) => {
        const strKey = this.keyHasher.hash(requestKey);
        return table.get(strKey);
      },
      affectedKeys: ({ entryKey, affectsKey }) => {
        // extract
        const key = this.keyHasher.unhash(entryKey) as LaserficheKey;
        // affect
        affectsKey(key);
      },
    });

    baseRepo.addRepo(this);
  }

  private listConnectionsKey(tenantId: string): LaserficheListConnectionsKey {
    return { type: "connections", tenantId };
  }
  private getIntegrationKey(
    tenantId: string,
    teamId: string | null
  ): LaserficheGetIntegrationKey {
    return { type: "integration", tenantId, teamId };
  }
  private getMetadataKey(
    tenantId: string,
    teamId: string | null
  ): LaserficheGetMetadataKey {
    return { type: "meta", tenantId, teamId };
  }
  private getDirectoryKey(
    tenantId: string,
    teamId: string | null,
    directoryId: number
  ): LaserficheGetDirectoryKey {
    return { type: "directory", tenantId, teamId, directoryId };
  }

  // FETCHERS

  /** List all Laserfiche connections configured for a tenant. */
  listConnections(
    tenantId: string,
    options?: GetObservableOptions
  ): Observable<LaserficheListConnectionsDto> {
    const key = this.listConnectionsKey(tenantId);
    return this.cache.observable(key, options);
  }

  /** Get all configured Laserfiche integration details: connection, directory setup, and
   * metadata. */
  getIntegration(
    tenantId: string,
    teamId: string | null,
    options?: GetObservableOptions
  ): Observable<LaserficheGetIntegrationDto> {
    const key = this.getIntegrationKey(tenantId, teamId);
    return this.cache.observable(key, options);
  }

  /** Get available metadata from Laserfiche. */
  getMetadata(
    tenantId: string,
    teamId: string | null,
    options?: GetObservableOptions
  ): Observable<LaserficheGetMetadataDto> {
    const key = this.getMetadataKey(tenantId, teamId);
    return this.cache.observable(key, options);
  }

  /** List entries in a directory in Laserfiche. */
  getDirectory(
    tenantId: string,
    teamId: string | null,
    directoryId: number = 1,
    options?: GetObservableOptions
  ): Observable<LaserficheGetDirectoryDto> {
    const key = this.getDirectoryKey(tenantId, teamId, directoryId);
    return this.cache.observable(key, options);
  }

  // MUTATORS

  /** Create or update Laserfiche connection config. */
  async createConnection(
    tenantId: string,
    teamId: string | null,
    connection: LaserficheConnection
  ): Promise<boolean> {
    let success: boolean = false;
    const query = teamQuery(teamId);

    if (connection.hostingType === "self") {
      success = (
        await apiService.v1.integrationLaserficheCreateSelfhosted(
          tenantId,
          omitFields(connection, ["hostingType"]),
          query
        )
      ).data;
    } else if (connection.hostingType === "cloud") {
      success = (
        await apiService.v1.integrationLaserficheCreateCloudhosted(
          tenantId,
          omitFields(connection, ["hostingType"]),
          query
        )
      ).data;
    }

    if (success) {
      this.invalidateListConnections(tenantId, true);
      this.invalidateGetIntegration(tenantId, teamId, true);
    }
    return success;
  }

  /** Delete a Laserfiche connection. */
  async deleteConnection(
    tenantId: string,
    teamId: string | null
  ): Promise<boolean> {
    const query = teamQuery(teamId);

    const success = (
      await apiService.v1.integrationLaserficheDeleteConnection(tenantId, query)
    ).data;

    if (success) {
      this.invalidateListConnections(tenantId, true);
      this.invalidateGetIntegration(tenantId, teamId, true);
    }
    return success;
  }

  /** Update Laserfiche directory config. */
  async updateDirectory(
    tenantId: string,
    teamId: string | null,
    directoryId: number,
    destinationName: keyof LaserficheConfiguration["output"]
  ): Promise<boolean> {
    const query = teamQuery(teamId);
    const success = (
      await apiService.v1.integrationLaserficheConfigDirectory(
        tenantId,
        { directoryId, destinationName },
        query
      )
    ).data;

    if (success) {
      this.invalidateGetIntegration(tenantId, teamId, true);
    }
    return success;
  }

  /** Update Laserfiche metadata config. */
  async updateMetadata(
    tenantId: string,
    teamId: string | null,
    metadata: UpdateLaserficheMetadata
  ): Promise<boolean> {
    const query = teamQuery(teamId);
    const success = (
      await apiService.v1.integrationLaserficheConfigMetadata(
        tenantId,
        {
          type: metadata.key.type,
          subType: metadata.key.subType as APIPushType | APIUploadType,
          config: metadata.config,
        },
        query
      )
    ).data;

    if (success) {
      this.invalidateGetIntegration(tenantId, teamId, true);
    }
    return success;
  }

  /** Delete Laserfiche internal config. */
  async deleteMetadata(
    tenantId: string,
    teamId: string | null,
    metadataKey: LaserficheMetadataConfigKey
  ): Promise<boolean> {
    const query = teamQuery(teamId);
    const success = (
      await apiService.v1.integrationLaserficheDeleteConfigMetadata(tenantId, {
        ...query,
        ...metadataKey,
      })
    ).data;

    if (success) {
      this.invalidateGetIntegration(tenantId, teamId, true);
    }
    return success;
  }

  /** Update Laserfiche internal config. */
  async updateInternal(
    tenantId: string,
    teamId: string | null,
    internalConfig: LaserficheInternalConfiguration
  ): Promise<void> {
    const query = teamQuery(teamId);
    await apiService.v1.integrationLaserficheUpdateInternalConfig(
      tenantId,
      internalConfig,
      query
    );

    this.invalidateGetIntegration(tenantId, teamId, true);
  }

  // INVALIDATORS

  /** Mark a list of connections as invalid. If `refetch` is true, and the data has loaded,
   * triggers a refetch. */
  invalidateListConnections(tenantId: string, refetch: boolean = false) {
    const key = this.listConnectionsKey(tenantId);
    const strKey = this.keyHasher.hash(key);

    refetch = refetch && this.cache.table.has(strKey);

    this.cache.invalidateEntry(strKey);

    if (refetch) this.cache.fetch(key);
  }

  /** Mark an integration as invalid. If `refetch` is true, and the data has loaded, triggers a
   * refetch. */
  invalidateGetIntegration(
    tenantId: string,
    teamId: string | null,
    refetch: boolean = false
  ) {
    const key = this.getIntegrationKey(tenantId, teamId);
    const strKey = this.keyHasher.hash(key);

    refetch = refetch && this.cache.table.has(strKey);

    this.cache.invalidateEntry(strKey);

    if (refetch) this.cache.fetch(key);
  }

  /** Mark metadata as invalid. If `refetch` is true, and the data has loaded, triggers a
   * refetch. */
  invalidateGetMetadata(
    tenantId: string,
    teamId: string | null,
    refetch: boolean = false
  ) {
    const key = this.getMetadataKey(tenantId, teamId);
    const strKey = this.keyHasher.hash(key);

    refetch = refetch && this.cache.table.has(strKey);

    this.cache.invalidateEntry(strKey);

    if (refetch) this.cache.fetch(key);
  }

  /** Mark a directory listing as invalid. If `refetch` is true, and the data has loaded,
   * triggers a refetch. */
  invalidateGetDirectory(
    tenantId: string,
    teamId: string | null,
    directoryId: number,
    refetch: boolean = false
  ) {
    const key = this.getDirectoryKey(tenantId, teamId, directoryId);
    const strKey = this.keyHasher.hash(key);

    refetch = refetch && this.cache.table.has(strKey);

    this.cache.invalidateEntry(strKey);

    if (refetch) this.cache.fetch(key);
  }

  /** Mark all directory listings for a tenant / team as invalid. If `refetch` is true, triggers
   * a refetch for all loaded directories. */
  invalidateAllDirectories(
    tenantId: string,
    teamId: string | null,
    refetch: boolean = false
  ) {
    const pattern = this.getDirectoryKey(tenantId, teamId, undefined!);
    const keys = this.cache.getMatchingKeys(pattern);

    for (const key of keys) {
      const strKey = this.keyHasher.hash(key);
      this.cache.invalidateEntry(strKey);
    }
    if (refetch) {
      for (const key of keys) {
        this.cache.fetch(key);
      }
    }
  }

  /** Clear all local cache. */
  invalidateAll() {
    this.cache.clear();
  }
}

export const laserficheIntegrationRepo = new LaserficheIntegrationRepository();
